import { COLOR_TOKENS } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Card, CardHeader, Dialog, Field, IconButton, Input, Select, Switch, useConfirm, useToast } from '../../../components/ui';
import { ApiError, api, errorMessage } from '../../../lib/api';

/** The union of fields the admin-managed lookups expose; each editor uses the ones it has. */
export type LookupItem = {
  id: string;
  key?: string;
  label?: string;
  name?: string;
  color?: string;
  isActive?: boolean;
  kind?: string;
  blocksOutreach?: boolean;
  isSystem?: boolean;
  isBuiltIn?: boolean;
  leadCount?: number;
  productCount?: number;
  interestCount?: number;
  scoringRuleCount?: number;
};

export type LookupConfig = {
  title: string;
  description?: string;
  endpoint: string;
  nameField: 'label' | 'name';
  color?: boolean;
  activeToggle?: boolean;
  kindOptions?: readonly { value: string; label: string }[];
  blocksOutreach?: boolean;
  reorder?: boolean;
  /** Deleting moves the leads to another entry (stages, statuses) instead of a plain confirmation. */
  reassignBodyKey?: 'reassignToStageId' | 'reassignToStatusId';
  usage: (item: LookupItem) => string;
};

const isLocked = (item: LookupItem) => item.isSystem === true || item.isBuiltIn === true;

export function LookupEditor({ config }: { config: LookupConfig }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const queryKey = ['settings', 'lookup', config.endpoint];
  const { data } = useQuery({ queryKey, queryFn: ({ signal }) => api.get<{ items: LookupItem[] }>(config.endpoint, { signal }) });
  const [newName, setNewName] = useState('');
  const [reassigning, setReassigning] = useState<LookupItem | null>(null);
  const [target, setTarget] = useState('');
  const items = data?.items ?? [];

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey });
    await queryClient.invalidateQueries({ queryKey: ['meta'] });
  };
  const onError = (error: unknown) => toast.error(errorMessage(error));
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => api.patch(`${config.endpoint}/${id}`, patch),
    onSuccess: refresh,
    onError,
  });
  const create = useMutation({
    mutationFn: () => api.post(config.endpoint, { [config.nameField]: newName.trim() }),
    onSuccess: async () => {
      setNewName('');
      await refresh();
    },
    onError,
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put(`${config.endpoint}/order`, { ids }),
    onSuccess: refresh,
    onError,
  });
  const remove = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api.delete(`${config.endpoint}/${id}`, body),
    onSuccess: async () => {
      setReassigning(null);
      toast.success('Deleted');
      await refresh();
    },
    onError: async (error, variables) => {
      // The API explains what a delete would affect; ask once, then retry with the admin's confirmation.
      if (error instanceof ApiError && error.status === 409 && typeof error.details === 'object' && error.details !== null && 'requiresConfirmation' in error.details) {
        if (await confirm({ title: 'Delete anyway?', message: error.message, confirmLabel: 'Delete', danger: true })) remove.mutate({ id: variables.id, body: { confirm: true } });
        return;
      }
      if (error instanceof ApiError && error.status === 409 && typeof error.details === 'object' && error.details !== null && 'requiresReassignment' in error.details) {
        setReassigning(items.find((i) => i.id === variables.id) ?? null);
        return;
      }
      onError(error);
    },
  });

  const move = (index: number, by: -1 | 1) => {
    const ids = items.map((i) => i.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(index + by, 0, moved!);
    reorder.mutate(ids);
  };
  const nameOf = (item: LookupItem) => (config.nameField === 'label' ? item.label : item.name) ?? '';

  return (
    <Card>
      <CardHeader title={config.title} description={config.description} />
      <ul className="divide-y divide-slate-100">
        {items.map((item, index) => (
          <li key={item.id} className="flex flex-wrap items-center gap-2 px-4 py-2 text-[13px]">
            <Input
              size="sm"
              aria-label="Name"
              className="w-56"
              defaultValue={nameOf(item)}
              onBlur={(event) => {
                const value = event.target.value.trim();
                if (value && value !== nameOf(item)) update.mutate({ id: item.id, patch: { [config.nameField]: value } });
              }}
            />
            {item.key && <code className="text-xs text-slate-400">{item.key}</code>}
            {config.color && (
              <Select size="sm" aria-label="Color" value={item.color ?? 'slate'} onChange={(event) => update.mutate({ id: item.id, patch: { color: event.target.value } })}>
                {COLOR_TOKENS.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </Select>
            )}
            {config.kindOptions && (
              <Select size="sm" aria-label="Kind" value={item.kind ?? ''} disabled={item.isSystem === true} onChange={(event) => update.mutate({ id: item.id, patch: { kind: event.target.value } })}>
                {config.kindOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
            {config.blocksOutreach && (
              <Switch size="sm" checked={item.blocksOutreach === true} onChange={(next) => update.mutate({ id: item.id, patch: { blocksOutreach: next } })} label="Blocks outreach" />
            )}
            {config.activeToggle && <Switch size="sm" checked={item.isActive !== false} onChange={(next) => update.mutate({ id: item.id, patch: { isActive: next } })} label="Active" />}
            {isLocked(item) && <Badge size="sm">Built-in</Badge>}
            <span className="ml-auto text-xs text-slate-500">{config.usage(item)}</span>
            {config.reorder && (
              <>
                <IconButton icon={ArrowUp} label="Move up" size="sm" variant="ghost" disabled={index === 0} onClick={() => move(index, -1)} />
                <IconButton icon={ArrowDown} label="Move down" size="sm" variant="ghost" disabled={index === items.length - 1} onClick={() => move(index, 1)} />
              </>
            )}
            <IconButton icon={Trash2} label={`Delete ${nameOf(item)}`} size="sm" variant="ghost" disabled={isLocked(item)} onClick={() => remove.mutate({ id: item.id, body: {} })} />
          </li>
        ))}
      </ul>
      <form
        className="flex gap-2 border-t border-slate-100 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (newName.trim()) create.mutate();
        }}
      >
        <Input size="sm" value={newName} onChange={(event) => setNewName(event.target.value)} placeholder={`New ${config.title.toLowerCase().replace(/s$/, '')}…`} aria-label="New entry name" />
        <Button size="sm" type="submit" icon={Plus} loading={create.isPending} disabled={!newName.trim()}>
          Add
        </Button>
      </form>

      <Dialog
        open={reassigning !== null}
        onClose={() => setReassigning(null)}
        title={`Delete “${reassigning ? nameOf(reassigning) : ''}”`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setReassigning(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!target}
              loading={remove.isPending}
              onClick={() => reassigning && config.reassignBodyKey && remove.mutate({ id: reassigning.id, body: { [config.reassignBodyKey]: target } })}
            >
              Move leads and delete
            </Button>
          </>
        }
      >
        <p className="mb-3 text-[13px] text-slate-600">{reassigning?.leadCount} lead(s) use it. Where should they go? Each move is recorded on the lead's timeline.</p>
        <Field label="Move them to">
          <Select value={target} onChange={(event) => setTarget(event.target.value)} placeholder="Choose…">
            {items
              .filter((i) => i.id !== reassigning?.id && i.isActive !== false)
              .map((i) => (
                <option key={i.id} value={i.id}>
                  {nameOf(i)}
                </option>
              ))}
          </Select>
        </Field>
      </Dialog>
    </Card>
  );
}
